"use client"
import { useState, useEffect, useCallback } from "react"
import DataTable, { type TableColumn } from "react-data-table-component"
import EntryReportingService from "../../../Service/entryReportingService"

import type { VehicleReportData, ReportQueryParams } from "./types"
import { format } from "date-fns"
import { Card, CardBody, CardHeader, CardTitle, Button, Badge, Row, Col } from "reactstrap"
import { FaCalendarAlt, FaDownload, FaSpinner } from "react-icons/fa"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

export function VehicleReportList() {
  const [data, setData] = useState<VehicleReportData[]>([])
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().setMonth(new Date().getMonth() - 1)))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const pageSize = 10

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.")
      return
    }

    setLoading(true)
    try {
      const params: ReportQueryParams & { page?: number; pageSize?: number } = {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        page: currentPage,
        pageSize: pageSize,
      }
      const response = await EntryReportingService.getVehicleReport(params)
      setData(response.results)
      setTotalRows(response.count)
    } catch (error) {
      console.error("Failed to fetch vehicle report:", error)
      toast.error("Failed to fetch vehicle report data.")
      setData([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, currentPage, pageSize])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    // react-data-table-component handles page size internally,
    // but if we were to implement server-side pagination with changing page sizes,
    // we would update pageSize state here and refetch.
    // For now, we'll keep pageSize fixed at 10 as per backend pagination.
  }

  const handleApplyFilter = () => {
    setCurrentPage(1) // Reset to first page on filter apply
    fetchReport()
  }

  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates for export.")
      return
    }

    setExportLoading(true)
    try {
      const params: ReportQueryParams = {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
      }
      const blob = await EntryReportingService.exportVehicleReport(params)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `vehicle_report_${format(startDate, "yyyyMMdd")}_${format(endDate, "yyyyMMdd")}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success("Vehicle report data exported as CSV.")
    } catch (error) {
      console.error("Failed to export vehicle report:", error)
      toast.error("Failed to export vehicle report.")
    } finally {
      setExportLoading(false)
    }
  }, [startDate, endDate])

  const columns: TableColumn<VehicleReportData>[] = [
    
    { name: "Plate", selector: (row: VehicleReportData) => row.plate, sortable: true },
    { name: "Amount", cell: (row: VehicleReportData) => `FRW ${row.amount.toFixed(2)}`, sortable: true },
    { name: "Phone Number", selector: (row: VehicleReportData) => row.phone_number, sortable: true },
    
    {
      name: "Status",
      cell: (row: VehicleReportData) => (
        <Badge color={row.status === "entered" ? "primary" : "secondary"}>{row.status}</Badge>
      ),
      sortable: true,
    },
    {
      name: "Entered At",
      cell: (row: VehicleReportData) => format(new Date(row.entered_at), "PPP"),
      sortable: true,
    },
    {
      name: "Exited At",
      cell: (row: VehicleReportData) => (row.exited_at ? format(new Date(row.exited_at), "PPP") : "N/A"),
      sortable: true,
    },
    {
      name: "Elapsed Time",
      selector: (row: VehicleReportData) => row.elapsed_time,
      sortable: true,
    },
  ]

  return (
    <Card className="shadow mb-4">
      <CardHeader className="d-flex justify-content-between align-items-center py-3">
        <div>
          <CardTitle tag="h5" className="mb-0">
            Vehicle Report
          </CardTitle>
          <p className="text-muted mb-0">Detailed list of vehicle visits within the selected date range.</p>
        </div>
        <Button color="success" onClick={handleExport} disabled={exportLoading || loading} size="sm">
          {exportLoading ? <FaSpinner className="fa-spin me-2" /> : <FaDownload className="me-2" />}
          Export CSV
        </Button>
      </CardHeader>
      <CardBody>
        <Row className="align-items-end mb-4 g-3">
          <Col md="auto">
            <label htmlFor="startDate" className="form-label mb-1">
              Start Date
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <FaCalendarAlt />
              </span>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                dateFormat="PPP"
                className="form-control"
                disabled={loading || exportLoading}
              />
            </div>
          </Col>
          <Col md="auto">
            <label htmlFor="endDate" className="form-label mb-1">
              End Date
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <FaCalendarAlt />
              </span>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                dateFormat="PPP"
                className="form-control"
                disabled={loading || exportLoading}
              />
            </div>
          </Col>
          <Col md="auto">
            <Button color="primary" onClick={handleApplyFilter} disabled={loading || exportLoading}>
              Apply Filter
            </Button>
          </Col>
        </Row>

        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={pageSize}
          paginationComponentOptions={{
            noRowsPerPage: true,
          }}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          pointerOnHover
          noDataComponent={
            <div className="text-center py-5 text-muted">No data available for the selected date range.</div>
          }
          progressComponent={
            <div className="text-center py-5">
              <FaSpinner className="fa-spin fa-3x text-primary" />
              <p className="mt-3 text-muted">Loading report data...</p>
            </div>
          }
        />
      </CardBody>
      <ToastContainer position="bottom-right" />
    </Card>
  )
}
